import React, { useState, useEffect, useRef } from 'react';
import './WelcomePage.css';
import LoginForm from './LoginForm';
import SyncDataModal from './SyncDataModal';
import { apiService } from '../apiService';

const Header = ({ user, onLogin, onLogout, theme, currentPage = 'chat', onPageChange, onOpenProfile, onOpenAbout }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Fetch user profile to check role
  useEffect(() => {
    if (user) {
      apiService.getUserProfile()
        .then(profile => {
          setUserRole(profile.role || 'user');
        })
        .catch(err => {
          console.error('Error fetching user profile:', err);
          setUserRole('user');
        });
    }
  }, [user]);

  const handleLoginSuccess = (userData) => {
    onLogin(userData);
    setShowLogin(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleProfileClick = () => {
    setShowSettingsDropdown(false);
    if (onOpenProfile) {
      onOpenProfile();
    }
  };

  const handleSettingsItemClick = (action) => {
    setShowSettingsDropdown(false);
    
    switch (action) {
      case 'logout':
        onLogout();
        break;
      case 'profile':
        if (onOpenProfile) {
          onOpenProfile();
        }
        break;
      case 'share':
        setShowShareModal(true);
        break;
      case 'about':
        if (onOpenAbout) {
          onOpenAbout();
        }
        break;
      case 'contact':
        if (onPageChange) {
          onPageChange('contact');
        }
        break;
      case 'sync':
        setShowSyncModal(true);
        break;
      case 'adminQueries':
        if (onPageChange) {
          onPageChange('admin-pending-queries');
        }
        break;
      default:
        break;
    }
  };

  const isWhiteTheme = theme?.secondary === '#ffffff';
  
  return (
    <>
      <header 
        className={`header ${isWhiteTheme ? 'header-white-theme' : ''}`} 
        style={{ '--theme-color': theme?.secondary, '--theme-accent': theme?.accent }}
      >
        <div className="header-logo">
          <img src="/xiquet_images/xiquet_logo.png" alt="Xiquet.cat" width="55" height="55" />
          <h1>Xiquet.cat</h1>
        </div>
        <div className="header-nav">
          <nav className="header-nav-buttons">
            {user ? (
              <>
                <button 
                  className={`header-nav-btn ${currentPage === 'chat' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('chat')}
                >
                  Xat
                </button>
                <button 
                  className={`header-nav-btn ${currentPage === 'joc-del-mocador' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('joc-del-mocador')}
                >
                  El Joc del Mocador
                </button>
                <button 
                  className={`header-nav-btn ${currentPage === 'colles-castelleres' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('colles-castelleres')}
                >
                  Colles Castelleres
                </button>
                <button 
                  className={`header-nav-btn ${currentPage === 'comparar-diades' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('comparar-diades')}
                >
                  Comparar Diades
                </button>
              </>
            ) : (
              <>
                <button 
                  className={`header-nav-btn ${currentPage === 'chat' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('chat')}
                  title="Tornar a la pàgina d’inici"
                >
                  Xat
                </button>
                <button 
                  className="header-nav-btn"
                  onClick={() => setShowLogin(true)}
                  title="Cal iniciar sessió"
                >
                  El Joc del Mocador
                </button>
                <button 
                  className={`header-nav-btn ${currentPage === 'colles-castelleres' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('colles-castelleres')}
                >
                  Colles Castelleres
                </button>
                <button 
                  className={`header-nav-btn ${currentPage === 'comparar-diades' ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange('comparar-diades')}
                >
                  Comparar Diades
                </button>
              </>
            )}
          </nav>
        </div>
        <div className="user-info">
          {user ? (
            <div className="user-controls">
              {/* Profile Avatar Button */}
              <button 
                className="profile-avatar-btn"
                onClick={handleProfileClick}
                title={`Perfil de ${user.username}`}
              >
                <span 
                  className={`profile-avatar-circle ${isWhiteTheme ? 'white-theme' : ''}`}
                  style={{ backgroundColor: isWhiteTheme ? '#ffffff' : (theme?.secondary || '#d0282c') }}
                >
                  {getInitials(user.username)}
                </span>
              </button>

              {/* Settings Button with Dropdown */}
              <div className="settings-dropdown-container" ref={dropdownRef}>
                <button 
                  className={`settings-btn ${showSettingsDropdown ? 'active' : ''}`}
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  title="Configuració"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>

                {showSettingsDropdown && (
                  <div className="settings-dropdown">
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('profile')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Perfil
                    </button>
                    
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('share')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      Compartir Xiquet
                    </button>
                    
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('about')}
                    >
                      <img 
                        src="/xiquet_images/xiquet_logo_zoom.png" 
                        alt="Xiquet" 
                        width="16" 
                        height="16" 
                        style={{ objectFit: 'contain' }}
                      />
                      Sobre Xiquet.cat
                    </button>
                    
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('contact')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Contacte
                    </button>
                    
                    {userRole === 'admin' && (
                      <>
                        <div className="settings-dropdown-divider"></div>
                        <button 
                          className="settings-dropdown-item"
                          onClick={() => handleSettingsItemClick('sync')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                          </svg>
                          Sincronitzar Noves Dades
                        </button>
                        <button
                          type="button"
                          className="settings-dropdown-item"
                          onClick={() => handleSettingsItemClick('adminQueries')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 3v18h18" />
                            <path d="M7 16V9" />
                            <path d="M12 16v-5" />
                            <path d="M17 16V6" />
                          </svg>
                          Seguiment consultes
                        </button>
                      </>
                    )}
                    
                    <div className="settings-dropdown-divider"></div>
                    
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('logout')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Sortir
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="user-controls">
              {/* Settings (Compartir, Sobre, Contacte) - accessible to everyone */}
              <div className="settings-dropdown-container" ref={dropdownRef}>
                <button 
                  className={`settings-btn ${showSettingsDropdown ? 'active' : ''}`}
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  title="Configuració"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>

                {showSettingsDropdown && (
                  <div className="settings-dropdown">
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('share')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      Compartir Xiquet
                    </button>
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('about')}
                    >
                      <img 
                        src="/xiquet_images/xiquet_logo_zoom.png" 
                        alt="Xiquet" 
                        width="16" 
                        height="16" 
                        style={{ objectFit: 'contain' }}
                      />
                      Sobre Xiquet.cat
                    </button>
                    <button 
                      className="settings-dropdown-item"
                      onClick={() => handleSettingsItemClick('contact')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Contacte
                    </button>
                  </div>
                )}
              </div>
              <button 
                className="header-login-btn"
                onClick={() => setShowLogin(true)}
              >
                Entrar
              </button>
            </div>
          )}
        </div>
        
        {showLogin && (
          <div className="login-modal-overlay" onClick={() => setShowLogin(false)}>
            <div className="login-modal" onClick={(e) => e.stopPropagation()}>
              <LoginForm 
                onLogin={handleLoginSuccess}
                onClose={() => setShowLogin(false)}
              />
            </div>
          </div>
        )}
      </header>
      <div className="header-border-bottom"></div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="share-modal-close"
              onClick={() => setShowShareModal(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            
            
            <h2>Comparteix Xiquet.cat</h2>

            <p className="share-modal-options-label">Compartir amb</p>
            <div className="share-modal-options">
              <a
                href={`mailto:?subject=${encodeURIComponent('Comparteix Xiquet.cat')}&body=${encodeURIComponent('Mira Xiquet.cat: https://www.xiquet.cat')}`}
                className="share-modal-option-btn share-modal-option-email"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Email
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent('Comparteix Xiquet.cat: https://www.xiquet.cat')}`}
                className="share-modal-option-btn share-modal-option-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>
            
            <div className="share-modal-link-container">
              <input 
                type="text" 
                value="www.xiquet.cat" 
                readOnly 
                className="share-modal-link-input"
              />
              <button 
                className={`share-modal-copy-btn ${copied ? 'copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText('https://www.xiquet.cat');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {!isMobile && ' Copiat!'}
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    {!isMobile && ' Copiar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Data Modal */}
      {showSyncModal && (
        <SyncDataModal
          user={user}
          onClose={() => setShowSyncModal(false)}
          theme={theme}
        />
      )}
    </>
  );
};

export default Header;
